import dayjs from 'dayjs';
import { useFormContext } from 'react-hook-form';
import { useEditPageContext } from '../EditPage';
import { TextField as MuiTextField } from '@mui/material';
import { useMemo } from 'react';

export interface withReadModeFieldPropsBase {
  name: string;
  label: string;
  value?: any;
  options?: any[];
  valueGetter?: string;
  labelGetter?: string;
}

const DefaultReadModeRenderer: React.FC<withReadModeFieldPropsBase> = (
  props
) => {
  const { getValues } = useFormContext();

  if (props.options && props.valueGetter && props.labelGetter) {
    const selected = props.options?.find(
      (item) => item[`${props.valueGetter}`] === getValues(props.name)
    );

    return (
      <DefaultReadModeComponent
        {...props}
        value={selected ? selected[props.labelGetter] : undefined}
      />
    );
  }

  return <DefaultReadModeComponent {...props} />;
};

export const DefaultReadModeComponent: React.FC<withReadModeFieldPropsBase> = ({
  label,
  name,
  value
}) => {
  const { getValues } = useFormContext();

  const renderValue = useMemo(() => {
    if (value) {
      return value;
    }

    let value2 = getValues(name) || '—';
    if (dayjs.isDayjs(value2)) {
      value2 = dayjs(value2).isValid()
        ? dayjs(value2).format('DD-MM-YYYY')
        : '—';
    }
    return value2;
  }, [name, getValues(name)]);

  return (
    <MuiTextField
      variant="outlined"
      label={label}
      value={renderValue}
      fullWidth
      disabled={true}
      size="small"
      className="read_mode"
    />
  );
};

const withReadModeField =
  <CProps extends object>(Component: React.FC<CProps>) =>
  (
    props: CProps &
      withReadModeFieldPropsBase & {
        readModeRenderer?: (
          props: withReadModeFieldPropsBase & any
        ) => JSX.Element;
      }
  ) => {
    const { isEditable } = useEditPageContext();

    if (!isEditable) {
      if (props.readModeRenderer) {
        return props.readModeRenderer({
          name: props.name,
          label: props.label
        });
      }

      return (
        <DefaultReadModeRenderer
          name={props.name}
          label={props.label}
          options={props.options}
          labelGetter={props.labelGetter}
          valueGetter={props.valueGetter}
        />
      );
    }

    return <Component {...props} disabled={!isEditable} />;
  };

export default withReadModeField;
